import { db } from '~/config/firebase.config';
import {
    collection,
    deleteDoc,
    doc,
    getDoc,
    getDocs,
    limit,
    onSnapshot,
    orderBy,
    query,
    serverTimestamp,
    setDoc,
    updateDoc,
    where,
} from 'firebase/firestore';

export type OfferDoc = {
    id: string;
    title: string;
    shopName?: string;
    shopPlace?: string;
    active?: boolean;
    trigger?: 'daily' | 'weekly' | 'monthly' | 'yearly' | 'all-time' | 'participation';
    validityDelay?: '1h' | '3h' | '12h' | '1d';
    createdAt?: Date;
    updatedAt?: Date;
};

const getOffersRef = (clientID: string) => collection(db, 'clients', clientID, 'offers');

function removeUndefined(obj: any): any {
    if (Array.isArray(obj)) {
        return obj.map(removeUndefined);
    }
    if (obj !== null && typeof obj === 'object') {
        return Object.entries(obj).reduce((acc, [key, value]) => {
            if (value !== undefined) {
                acc[key] = removeUndefined(value);
            }
            return acc;
        }, {} as any);
    }
    return obj;
}

async function saveOffer(clientID: string, offerId: string, offerData: any): Promise<void> {
    const offerRef = doc(db, 'clients', clientID, 'offers', offerId);

    try {
        const document = await getDoc(offerRef);
        const cleanedData = removeUndefined(offerData);
        const dataToSave = {
            ...cleanedData,
            updatedAt: serverTimestamp(),
        };

        if (document.exists()) {
            await updateDoc(offerRef, dataToSave);
        } else {
            await setDoc(offerRef, {
                ...dataToSave,
                createdAt: serverTimestamp(),
            });
        }
    } catch (err) {
        console.error('Cannot save offer:', err);
        throw err;
    }
}

async function retrieveOffer(clientID: string, offerId: string): Promise<any> {
    const offerRef = doc(db, 'clients', clientID, 'offers', offerId);

    try {
        const document = await getDoc(offerRef);
        if (document.exists()) {
            const data = document.data();
            return {
                ...data,
                id: document.id,
                createdAt:
                    (data as any).createdAt && typeof (data as any).createdAt.toDate === 'function'
                        ? (data as any).createdAt.toDate()
                        : new Date(),
                updatedAt:
                    (data as any).updatedAt && typeof (data as any).updatedAt.toDate === 'function'
                        ? (data as any).updatedAt.toDate()
                        : new Date(),
            };
        }
        return null;
    } catch (err) {
        console.error('Cannot retrieve offer:', err);
        throw err;
    }
}

async function retrieveOffers(clientID: string, total: number): Promise<any[]> {
    const limitValue = total <= 0 ? 10000 : total;
    const offersRef = getOffersRef(clientID);
    const q = query(offersRef, orderBy('updatedAt', 'desc'), limit(limitValue));

    try {
        const querySnapshot = await getDocs(q);
        const offers: any[] = [];

        querySnapshot.forEach((document) => {
            const data = document.data();
            offers.push({
                ...data,
                id: document.id,
                createdAt:
                    (data as any).createdAt && typeof (data as any).createdAt.toDate === 'function'
                        ? (data as any).createdAt.toDate()
                        : new Date(),
                updatedAt:
                    (data as any).updatedAt && typeof (data as any).updatedAt.toDate === 'function'
                        ? (data as any).updatedAt.toDate()
                        : new Date(),
            });
        });

        return offers;
    } catch (err) {
        console.error('Cannot retrieve offers:', err);
        throw err;
    }
}

async function deleteOffer(clientID: string, offerId: string): Promise<void> {
    const offerRef = doc(db, 'clients', clientID, 'offers', offerId);

    try {
        await deleteDoc(offerRef);
    } catch (err) {
        console.error('Cannot delete offer:', err);
        throw err;
    }
}

async function retrieveActiveHighscoreOffers(clientID: string, total: number): Promise<any[]> {
    const limitValue = total <= 0 ? 10000 : total;
    const offersRef = getOffersRef(clientID);
    const q = query(offersRef, where('active', '==', true), orderBy('updatedAt', 'desc'), limit(limitValue));

    try {
        const querySnapshot = await getDocs(q);
        const offers: any[] = [];

        querySnapshot.forEach((document) => {
            const data = document.data();
            // Filter by trigger being one of the highscore scope triggers
            const highscoreTriggers = ['daily', 'weekly', 'monthly', 'yearly', 'all-time'];
            if (typeof data.trigger === 'string' && highscoreTriggers.includes(data.trigger)) {
                offers.push({
                    ...data,
                    id: document.id,
                    createdAt:
                        (data as any).createdAt && typeof (data as any).createdAt.toDate === 'function'
                            ? (data as any).createdAt.toDate()
                            : new Date(),
                    updatedAt:
                        (data as any).updatedAt && typeof (data as any).updatedAt.toDate === 'function'
                            ? (data as any).updatedAt.toDate()
                            : new Date(),
                });
            }
        });

        return offers;
    } catch (err) {
        console.error('Cannot retrieve active highscore offers:', err);
        throw err;
    }
}

function subscribeToOffers(clientID: string) {
    const offersRef = getOffersRef(clientID);
    return (onValueChange: () => void) => onSnapshot(offersRef, onValueChange);
}

export const OffersAPI = {
    saveOffer,
    retrieveOffer,
    retrieveOffers,
    retrieveActiveHighscoreOffers,
    async retrieveActiveOffersByTrigger(clientID: string, trigger: string, total: number): Promise<any[]> {
        const limitValue = total <= 0 ? 10000 : total;
        const offersRef = getOffersRef(clientID);
        const q = query(
            offersRef,
            where('active', '==', true),
            where('trigger', '==', trigger),
            orderBy('updatedAt', 'desc'),
            limit(limitValue)
        );

        try {
            const querySnapshot = await getDocs(q);
            const offers: any[] = [];
            querySnapshot.forEach((document) => {
                const data = document.data();
                offers.push({
                    ...data,
                    id: document.id,
                    createdAt:
                        (data as any).createdAt && typeof (data as any).createdAt.toDate === 'function'
                            ? (data as any).createdAt.toDate()
                            : new Date(),
                    updatedAt:
                        (data as any).updatedAt && typeof (data as any).updatedAt.toDate === 'function'
                            ? (data as any).updatedAt.toDate()
                            : new Date(),
                });
            });

            return offers;
        } catch (err) {
            console.error('Cannot retrieve active offers by trigger:', err);
            throw err;
        }
    },
    deleteOffer,
    subscribeToOffers,
    ref: getOffersRef,
};
